// /src/components/chat/shared/MessagesBox.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, styled } from '@mui/material';
import MessageContainer from './MessageContainer';
import { CalcomEventTypePicker, CalcomSlotsPicker } from '../CalcomSchedulingPickers';
import { QuickReplyPicker } from '../QuickReplyPicker';

// Styled component to match the original ChatPage.js
const StyledMessagesBox = styled(Box)(({ backgroundColor }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  backgroundColor: backgroundColor,
}));

const MessagesBox = ({ messages, customization = {}, fontSize = 'medium', ws, setMessages, onSubmit, onSendMessage }) => {
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const format = customization.timestampFormat || 'MM/DD/YYYY hh:mm a';

    switch (format) {
      case 'hh:mm a':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      case 'HH:mm':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      case 'MM/DD/YYYY hh:mm a':
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      case 'DD/MM/YYYY HH:mm':
        return date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      case 'YYYY-MM-DD HH:mm':
        return date.getFullYear() + '-' +
          String(date.getMonth() + 1).padStart(2, '0') + '-' +
          String(date.getDate()).padStart(2, '0') + ' ' +
          date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
      default:
        return date.toLocaleTimeString();
    }
  };

  return (
    <StyledMessagesBox backgroundColor={customization.chatBackgroundColor}>
      {messages.map((msg, idx) => {
        if (msg.isForm) {
          return (
            <Box key={msg.id || idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <ChatFormMessage
                fields={msg.formFields}
                successMessage={msg.successMessage}
                onSubmit={values => onSubmit(values, msg.formFields)}
              />
            </Box>
          );
        }
        if (msg.calcomSlots && typeof onSendMessage === 'function') {
          return (
            <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              {msg.isBot && customization.agentName && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, mb: 0.5, fontWeight: 'bold', color: customization.agentNameColor || 'text.primary' }}>
                  {customization.agentName}
                </Typography>
              )}
              {customization.showTimestamps && msg.timestamp && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, color: customization.timestampColor || 'text.secondary' }}>
                  {formatTimestamp(msg.timestamp)}
                </Typography>
              )}
              {msg.text ? (
                <MessageContainer message={msg} customization={customization} fontSize={fontSize} />
              ) : null}
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', ml: 1, mr: 2 }}>
                <CalcomSlotsPicker payload={msg.calcomSlots} onSendMessage={onSendMessage} />
              </Box>
            </Box>
          );
        }
        if (msg.calcomEventTypes?.length && typeof onSendMessage === 'function') {
          return (
            <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              {msg.isBot && customization.agentName && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, mb: 0.5, fontWeight: 'bold', color: customization.agentNameColor || 'text.primary' }}>
                  {customization.agentName}
                </Typography>
              )}
              {customization.showTimestamps && msg.timestamp && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, color: customization.timestampColor || 'text.secondary' }}>
                  {formatTimestamp(msg.timestamp)}
                </Typography>
              )}
              {msg.text ? (
                <MessageContainer message={msg} customization={customization} fontSize={fontSize} />
              ) : null}
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', ml: 1, mr: 2 }}>
                <CalcomEventTypePicker
                  items={msg.calcomEventTypes}
                  onPick={(et, meta) =>
                    onSendMessage(meta.wireMessage, { displayMessage: meta.displayMessage })
                  }
                />
              </Box>
            </Box>
          );
        }
        if (msg.isBot && msg.quickReplies?.length && typeof onSendMessage === 'function') {
          return (
            <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              {customization.agentName && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, mb: 0.5, fontWeight: 'bold', color: customization.agentNameColor || 'text.primary' }}>
                  {customization.agentName}
                </Typography>
              )}
              {customization.showTimestamps && msg.timestamp && (
                <Typography variant="caption" sx={{ alignSelf: 'flex-start', mx: 2, color: customization.timestampColor || 'text.secondary' }}>
                  {formatTimestamp(msg.timestamp)}
                </Typography>
              )}
              {msg.text ? (
                <MessageContainer message={msg} customization={customization} fontSize={fontSize} />
              ) : null}
              <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', ml: 1, mr: 2 }}>
                <QuickReplyPicker
                  items={msg.quickReplies}
                  botMessageColor={customization.botMessageColor || '#f5f5f5'}
                  botTextColor={customization.botTextColor || '#000000'}
                  quickReplyTextColor={customization.quickReplyTextColor || '#1976d2'}
                  quickReplyBorderColor={customization.quickReplyBorderColor || '#1976d2'}
                  quickReplyBackgroundColor={customization.quickReplyBackgroundColor || '#FFFFFF'}
                  onPick={(item) => {
                    const value = (item.value || item.label || '').trim();
                    const label = (item.label || value).trim();
                    onSendMessage(value, {
                      ...(label && label !== value ? { displayMessage: label } : {}),
                      ...(item.action ? { action: item.action } : {}),
                      ...(item.pendingActionId ? { pendingActionId: item.pendingActionId } : {}),
                    });
                  }}
                />
              </Box>
            </Box>
          );
        }
        return (
          <Box key={idx} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Agent Name display for bot messages */}
            {msg.isBot && customization.agentName && (
              <Typography
                variant="caption"
                sx={{
                  alignSelf: 'flex-start',
                  mx: 2,
                  mb: 0.5,
                  color: customization.agentNameColor || 'text.primary',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {customization.agentName}
              </Typography>
            )}
            {customization.showTimestamps && msg.timestamp && (
              <Typography
                variant="caption"
                sx={{
                  alignSelf: msg.isBot ? 'flex-start' : 'flex-end',
                  mx: 2,
                  color: customization.timestampColor || 'text.secondary'
                }}
              >
                {formatTimestamp(msg.timestamp)}
              </Typography>
            )}
            <MessageContainer
              message={msg}
              customization={customization}
              fontSize={fontSize}
            />
          </Box>
        );
      })}
      <div ref={messagesEndRef} />
    </StyledMessagesBox>
  );
};



export const ChatFormMessage = ({ fields, onSubmit, successMessage }) => {
  const [values, setValues] = useState(
    fields.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {})
  );
  const [error, setError] = useState('');

  const handleChange = e => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };
  // No onBlur logic

  const handleSubmit = e => {
    e.preventDefault();
    // Basic email validation
    const emailField = fields.find(f => f.type === 'email');
    if (emailField) {
      const email = values[emailField.name];
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError('Please enter a valid email address.');
        return;
      }
    }
    setError('');
    onSubmit(values);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: '#f5f5f5',
        padding: 12,
        borderRadius: 8,
        margin: '8px 0',
        maxWidth: 500,
      }}
    >
      {fields.map(f => (
        <div
          key={f.name}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            marginBottom: 16,
            width: '100%',
          }}
        >
          <label
            htmlFor={f.name}
            style={{
              marginBottom: 6,
              fontWeight: 500,
              color: '#333',
              textAlign: 'left',
            }}
          >
            {f.label}
            {f.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
          </label>

          {f.type === 'textarea' ? (
            <textarea
              id={f.name}
              name={f.name}
              value={values[f.name]}
              onChange={handleChange}
              required={f.required}
              rows={f.rows || 3}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 4,
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                fontSize: 14,
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <input
              id={f.name}
              type={f.type}
              name={f.name}
              value={values[f.name]}
              onChange={handleChange}
              required={f.required}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 4,
                border: '1px solid #ccc',
                fontSize: 14,
                boxSizing: 'border-box',
              }}
            />
          )}
        </div>
      ))}


      {error && (
        <div style={{ color: 'red', marginBottom: 8 }}>{error}</div>
      )}

      <button type="submit">Submit</button>
    </form>

  );
};

export default MessagesBox;
